/**
 * Theme + brand helpers.
 *
 * Two independent axes:
 *  - tenant (which brand palette) — resolved at boot from localStorage (set by
 *    the dashboard SSO flow) with a hostname fallback, since the WhatsApp UI is
 *    served on multiple hostnames (agente.menteo.ai, agente.houdini.mx).
 *  - color mode (light / dark / follow the OS) — user preference, picked from
 *    the header settings menu.
 *
 * Tenant palettes live in src/app/globals.css as :root[data-tenant='…']
 * blocks and the dark token sheet under `.dark`; the provider only flips the
 * attribute/class. Setting both on documentElement (instead of a scoped
 * wrapper) also themes Radix portals, which render outside the React tree.
 * The chat surface is immune to tenant overrides because it uses the dedicated
 * --chat-* tokens — but it *does* follow the color mode.
 */

import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';

export enum ThemeType {
    MENTEO_THEME = 'menteo-theme',
    HOUDINI_THEME = 'houdini-theme',
}

export enum ColorMode {
    LIGHT = 'light',
    DARK = 'dark',
    SYSTEM = 'system',
}

/** Shared with menteo-dashboard through the common origin (/whatsapp/ proxy). */
const COLOR_MODE_KEY = 'color-mode';

const isColorMode = (value: string | null): value is ColorMode =>
    !!value && (Object.values(ColorMode) as string[]).includes(value);

const prefersDark = (): boolean =>
    window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;

/** Reads the stored preference; defaults to following the OS. */
export const getStoredColorMode = (): ColorMode => {
    const stored = localStorage.getItem(COLOR_MODE_KEY);
    return isColorMode(stored) ? stored : ColorMode.SYSTEM;
};

export const resolveColorMode = (mode: ColorMode): 'light' | 'dark' =>
    mode === ColorMode.SYSTEM ? (prefersDark() ? 'dark' : 'light') : mode;

/**
 * Flips the `.dark` class on <html>. Called from main.tsx before the first
 * render so the app never paints a light frame before hydrating.
 */
export const applyColorMode = (mode: ColorMode): void => {
    document.documentElement.classList.toggle('dark', resolveColorMode(mode) === 'dark');
    document.documentElement.style.colorScheme = resolveColorMode(mode);
};

const TENANT_ATTRS: Record<ThemeType, string | undefined> = {
    [ThemeType.MENTEO_THEME]: undefined,
    [ThemeType.HOUDINI_THEME]: 'houdini',
};

const LOGOS: Record<ThemeType, string> = {
    [ThemeType.MENTEO_THEME]: '/menteo-logo.svg',
    [ThemeType.HOUDINI_THEME]: '/houdini-logo.svg',
};

type ThemeContextType = {
    theme: ThemeType;
    colorMode: ColorMode;
    /** What `colorMode` actually resolves to right now (SYSTEM already applied). */
    resolvedColorMode: 'light' | 'dark';
    setColorMode: (mode: ColorMode) => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider = ({ children, theme }: { children: ReactNode; theme: ThemeType }) => {
    const [colorMode, setColorModeState] = useState<ColorMode>(getStoredColorMode);
    const [resolvedColorMode, setResolvedColorMode] = useState<'light' | 'dark'>(() =>
        resolveColorMode(getStoredColorMode()),
    );

    useEffect(() => {
        document.documentElement.dataset.theme = theme;
        const tenant = TENANT_ATTRS[theme] ?? TENANT_ATTRS[ThemeType.MENTEO_THEME];
        if (tenant) {
            document.documentElement.dataset.tenant = tenant;
        } else {
            delete document.documentElement.dataset.tenant;
        }
    }, [theme]);

    useEffect(() => {
        applyColorMode(colorMode);
        setResolvedColorMode(resolveColorMode(colorMode));
        if (colorMode !== ColorMode.SYSTEM) return;

        // Only relevant while following the OS: repaint when it switches.
        const query = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = () => {
            applyColorMode(ColorMode.SYSTEM);
            setResolvedColorMode(resolveColorMode(ColorMode.SYSTEM));
        };
        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
    }, [colorMode]);

    const setColorMode = useCallback((mode: ColorMode) => {
        localStorage.setItem(COLOR_MODE_KEY, mode);
        setColorModeState(mode);
    }, []);

    return (
        <ThemeContext.Provider value={{ theme, colorMode, resolvedColorMode, setColorMode }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

/** Color-mode state + setter for components rendered inside ThemeProvider. */
export const useColorMode = () => {
    const { colorMode, resolvedColorMode, setColorMode } = useTheme();
    return { colorMode, resolvedColorMode, setColorMode };
};

// Human-readable brand name per theme, used in user-facing copy.
export const BRAND_NAMES: Record<ThemeType, string> = {
    [ThemeType.MENTEO_THEME]: 'Menteo',
    [ThemeType.HOUDINI_THEME]: 'Houdini',
};

// Reads the active theme from localStorage (set by getTheme at boot).
// Usable outside React components; defaults to Menteo when unset/unknown.
export const getBrandName = (): string => {
    const theme = localStorage.getItem('theme') as ThemeType | null;
    return (theme && BRAND_NAMES[theme]) || BRAND_NAMES[ThemeType.MENTEO_THEME];
};

// Hook for components rendered inside ThemeProvider.
export const useBrandName = (): string => {
    const { theme } = useTheme();
    return BRAND_NAMES[theme] ?? BRAND_NAMES[ThemeType.MENTEO_THEME];
};

// Interpolates the active brand name into a static config string.
export const applyBrand = (text: string): string => text.replace(/\{brand\}/g, getBrandName());

// Returns the brand-aware logo URL for use in <img src>.
export const useLogoPath = (): string => {
    const { theme } = useTheme();
    return LOGOS[theme] ?? LOGOS[ThemeType.MENTEO_THEME];
};

// Dashboard origin. The same build serves Menteo and Houdini by hostname; the
// dashboard env points at menteo.ai by default, swap the suffix for Houdini.
export const getDashboardUrl = (): string | undefined => {
    const url = import.meta.env.VITE_DASHBOARD_URL;
    if (!url) return undefined;
    const theme = localStorage.getItem('theme') as ThemeType | null;
    if (theme === ThemeType.HOUDINI_THEME) return url.replace('menteo.ai', 'houdini.mx');
    return url;
};
